import { sortMetricNames } from '@/lib/metrics'
import { SectionHeader } from './SectionHeader'
import { TransposedDataGrid } from './TransposedDataGrid'
import type { CampaignMetricsItem } from '@/entities/dailyMetric'

interface CampaignMetricsTableProps {
  campaignMetrics: CampaignMetricsItem[]
}

export function CampaignMetricsTable({ campaignMetrics }: CampaignMetricsTableProps) {
  // Group metrics by metric name
  const metricsByName = campaignMetrics.reduce(
    (acc, metric) => {
      if (!acc[metric.name]) {
        acc[metric.name] = []
      }
      acc[metric.name].push(metric)
      return acc
    },
    {} as Record<string, CampaignMetricsItem[]>
  )

  const uniqueDates = Array.from(new Set(campaignMetrics.map((metric) => metric.date)))

  // Filter out list-related metrics (List X - ..., ... - List X) and Transfer % metrics
  const filteredMetricNames = Object.keys(metricsByName).filter((metricName) => {
    // Filter out metrics that contain " - List " or start with "List " (for list-specific metrics)
    const containsListPattern = metricName.includes(' - List ') || metricName.startsWith('List ')
    // Filter out Transfer % metrics
    const isTransferPercent = metricName.includes('Transfer %')
    return !containsListPattern && !isTransferPercent
  })

  // Create rows for each metric type, using sorted metric names
  const rows = sortMetricNames(filteredMetricNames).map((metricName, index) => {
    const row: Record<string, string | number> = {
      id: index,
      metric: metricName,
    }
    const metricItems = metricsByName[metricName]
    const isFloatMetric = metricItems.some((item) => !Number.isInteger(Number(item.value) || 0))

    // Add data for each date
    uniqueDates.forEach((dateStr) => {
      const metricForDate = metricItems.find((m) => m.date === dateStr)
      const value = metricForDate?.value

      if (value !== undefined && !isNaN(Number(value))) {
        const numValue = Number(value)
        if (numValue === 0) {
          row[dateStr] = ''
        } else if (metricName.includes('%') || isFloatMetric) {
          row[dateStr] = `${(numValue * 100).toFixed(2)}%`
        } else {
          row[dateStr] = value
        }
      } else {
        row[dateStr] = value !== undefined ? value : ''
      }
    })

    return row
  })

  return (
    <div className="h-max-[600px] flex flex-col w-full mt-8">
      <SectionHeader title="Campaign Metrics" />
      <div className="bg-white rounded-lg shadow-sm border border overflow-hidden">
        <TransposedDataGrid
          rows={rows}
          firstColumn={{
            field: 'metric',
            headerName: 'Metric',
          }}
          dates={uniqueDates}
        />
      </div>
    </div>
  )
}